import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Camera,
  FileText,
  User,
  Phone,
  MapPin,
  Briefcase,
  CreditCard,
  Building2,
  ExternalLink,
  Image,
  AlertCircle,
  Clock,
  Upload,
  Trash2,
  RefreshCw,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/EmployeeProfile.css";

function Field({ label, value }) {
  return (
    <div className='profile-field'>
      <span className='profile-field-label'>{label}</span>
      <span className={`profile-field-value ${!value ? "muted" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function Toast({ msg, type, onClose }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: type === "error" ? "#ef4444" : "#10b981",
        color: "#fff",
        padding: "10px 20px",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,.2)",
        zIndex: 9999,
        fontSize: "0.9rem",
      }}
    >
      {msg}
    </div>
  );
}

function OnboardingStep({
  icon: Icon,
  label,
  done,
  date,
  docInfo,
  showImage,
  onUpload,
  onDelete,
  uploading,
  accept,
}) {
  const [imgVisible, setImgVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);

  return (
    <div className={`onboarding-step ${done ? "done" : "missing"}`}>
      <div className={`onboarding-step-icon ${done ? "done" : "missing"}`}>
        {done ? <CheckCircle2 size={20} /> : <Icon size={20} />}
      </div>

      <div className='onboarding-step-info' style={{ flex: 1 }}>
        <div className='onboarding-step-label'>{label}</div>
        {done && date ? (
          <div className='onboarding-step-date'>
            {new Date(date).toLocaleDateString("he-IL")}
          </div>
        ) : (
          <div className='onboarding-step-missing'>לא הועלה</div>
        )}
        {/* inline image preview */}
        {done && showImage && docInfo?.url && imgVisible && (
          <div className='id-image-preview'>
            <img src={docInfo.url} alt={label} />
          </div>
        )}
      </div>

      <div
        className='onboarding-step-action'
        style={{ flexWrap: "wrap", justifyContent: "flex-end" }}
      >
        {/* view buttons */}
        {done && docInfo?.url && (
          <>
            {showImage && (
              <button
                className='profile-file-btn outline'
                onClick={() => setImgVisible((v) => !v)}
              >
                <Image size={13} /> {imgVisible ? "הסתר" : "הצג"}
              </button>
            )}
            <a
              href={docInfo.url}
              target='_blank'
              rel='noopener noreferrer'
              className='profile-file-btn primary'
            >
              <ExternalLink size={13} /> פתח
            </a>
          </>
        )}

        {/* upload / replace */}
        <button
          className='profile-file-btn outline'
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          title={done ? "החלף קובץ" : "העלה"}
        >
          {uploading ? (
            <RefreshCw
              size={13}
              style={{ animation: "spin .7s linear infinite" }}
            />
          ) : (
            <Upload size={13} />
          )}{" "}
          {done ? "החלף" : "העלה"}
        </button>

        {/* delete */}
        {done &&
          (confirmDelete ? (
            <>
              <button
                className='profile-file-btn'
                style={{ background: "#ef4444", color: "#fff" }}
                onClick={() => {
                  setConfirmDelete(false);
                  onDelete();
                }}
              >
                אשר מחיקה
              </button>
              <button
                className='profile-file-btn outline'
                onClick={() => setConfirmDelete(false)}
              >
                ביטול
              </button>
            </>
          ) : (
            <button
              className='profile-file-btn outline'
              style={{ color: "#ef4444", borderColor: "#ef4444" }}
              onClick={() => setConfirmDelete(true)}
              title='מחק קובץ'
            >
              <Trash2 size={13} /> מחק
            </button>
          ))}
      </div>

      {/* hidden file input */}
      <input
        ref={fileRef}
        type='file'
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.[0]) onUpload(e.target.files[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
}

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [mainEmp, setMainEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({
    id_image: false,
    contract: false,
    form101: false,
  });
  const [toast, setToast] = useState({ msg: "", type: "success" });

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const reload = async () => {
    try {
      const [profileRes, mainRes] = await Promise.all([
        apiService.getEmployeeOnboardingProfile(employeeId),
        apiService.getEmployee(employeeId),
      ]);
      setEmployee(profileRes.employee);
      setMainEmp(mainRes.employee || mainRes);
    } catch (e) {
      setError(e?.response?.data?.error || "שגיאה בטעינת הפרופיל");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    };
    load();
  }, [employeeId]); // eslint-disable-line

  const handleUpload = async (type, file) => {
    setUploading((u) => ({ ...u, [type]: true }));
    try {
      if (type === "id_image")
        await apiService.uploadOnboardingIdImage(employeeId, file);
      if (type === "contract")
        await apiService.uploadOnboardingContract(employeeId, file);
      if (type === "form101")
        await apiService.uploadOnboardingForm101(employeeId, file);
      showToast("הקובץ הועלה בהצלחה ✓");
      await reload();
    } catch (e) {
      showToast(e?.response?.data?.error || "שגיאה בהעלאה", "error");
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  };

  const handleDelete = async (type) => {
    try {
      if (type === "id_image")
        await apiService.deleteOnboardingIdImage(employeeId);
      if (type === "contract")
        await apiService.deleteOnboardingContract(employeeId);
      if (type === "form101")
        await apiService.deleteOnboardingForm101(employeeId);
      showToast("הקובץ נמחק");
      await reload();
    } catch (e) {
      showToast(e?.response?.data?.error || "שגיאה במחיקה", "error");
    }
  };

  if (loading) {
    return (
      <div className='profile-loading'>
        <div className='profile-spinner' />
        <span>טוען פרופיל עובד...</span>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className='profile-loading'>
        <AlertCircle size={40} color='#ef4444' />
        <span>{error || "עובד לא נמצא"}</span>
        <button className='profile-back-btn' onClick={() => navigate(-1)}>
          <ArrowRight size={16} /> חזרה
        </button>
      </div>
    );
  }

  const ob = employee.onboarding || {};
  const docs = employee.documents || {};
  const emp = mainEmp || employee;

  const doneCount = [
    ob.id_image_uploaded,
    ob.contract_signed,
    ob.form101_signed,
  ].filter(Boolean).length;
  const pct = Math.round((doneCount / 3) * 100);
  const initials = (name) =>
    name
      ?.split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("") || "?";

  const obBadgeClass = ob.onboarding_completed
    ? "badge-ob-done"
    : doneCount > 0
      ? "badge-ob-part"
      : "badge-ob-none";
  const obBadgeLabel = ob.onboarding_completed
    ? "✅ קליטה הושלמה"
    : doneCount > 0
      ? `🔶 ${doneCount}/3 הושלמו`
      : "⬜ טרם התחיל";

  return (
    <div className='employee-profile-page'>
      {/* Header */}
      <div className='profile-header'>
        <div className='profile-avatar'>{initials(employee.name)}</div>
        <div className='profile-header-info'>
          <h1 className='profile-name'>{employee.name}</h1>
          <div className='profile-sub'>
            {employee.employee_number && (
              <span>מס׳ {employee.employee_number}</span>
            )}
            {employee.phone && employee.phone !== "---" && (
              <span>
                <Phone size={13} /> {employee.phone}
              </span>
            )}
            {(employee.site || employee.customer_name) && (
              <span>
                <Building2 size={13} />{" "}
                {employee.site || employee.customer_name}
              </span>
            )}
          </div>
          <div className='profile-header-badges' style={{ marginTop: 8 }}>
            <span
              className={`profile-badge ${emp.active !== false ? "badge-active" : "badge-inactive"}`}
            >
              {emp.active !== false ? "פעיל" : "לא פעיל"}
            </span>
            {employee.type && (
              <span className='profile-badge badge-type-tag'>
                {employee.type}
              </span>
            )}
            <span className={`profile-badge ${obBadgeClass}`}>
              {obBadgeLabel}
            </span>
          </div>
        </div>
        <button className='profile-back-btn' onClick={() => navigate(-1)}>
          <ArrowRight size={16} /> חזרה לרשימה
        </button>
      </div>

      {/* Info grid */}
      <div className='profile-grid'>
        <div className='profile-card'>
          <div className='profile-card-title'>
            <User size={14} /> פרטים אישיים
          </div>
          <Field label='שם מלא' value={employee.name} />
          <Field label='מספר עובד' value={employee.employee_number} />
          <Field
            label='טלפון'
            value={employee.phone !== "---" ? employee.phone : null}
          />
          <Field label='כתובת' value={employee.address || emp.address} />
        </div>

        <div className='profile-card'>
          <div className='profile-card-title'>
            <Briefcase size={14} /> פרטי עסקה
          </div>
          <Field label='סוג' value={employee.type || emp.type} />
          <Field
            label='תעריף שעתי'
            value={emp.hour_rate ? `₪${emp.hour_rate}` : null}
          />
          <Field
            label='תעריף סוף שבוע'
            value={emp.weekend_hour_rate ? `₪${emp.weekend_hour_rate}` : null}
          />
          <Field label='אמצעי תשלום' value={emp.payment_method} />
        </div>

        <div className='profile-card'>
          <div className='profile-card-title'>
            <CreditCard size={14} /> פרטי בנק
          </div>
          <Field label='בנק' value={emp.bank} />
          <Field label='מספר חשבון' value={emp.account} />
          <Field label='סניף' value={emp.branch} />
          <Field label='שם בעל החשבון' value={emp.account_name} />
          <Field label='מספר מיכפל' value={emp.michpal_id} />
        </div>

        <div className='profile-card'>
          <div className='profile-card-title'>
            <MapPin size={14} /> אתרים ולקוחות
          </div>
          {employee.customers && employee.customers.length > 0 ? (
            employee.customers.map((c) => (
              <div key={c.id} className='profile-customer-row'>
                <Building2 size={14} color='#94a3b8' />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    {c.name}
                  </div>
                  {c.site && (
                    <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                      {c.site}
                    </div>
                  )}
                </div>
                {c.is_primary && (
                  <span className='profile-customer-primary'>ראשי</span>
                )}
              </div>
            ))
          ) : (
            <span className='profile-field-value muted'>
              אין לקוחות משויכים
            </span>
          )}
        </div>
      </div>

      {/* Onboarding tracker */}
      <div className='onboarding-tracker'>
        <div className='profile-card-title' style={{ marginBottom: 10 }}>
          <Clock size={14} /> מעקב קליטה — {doneCount}/3 שלבים הושלמו
        </div>
        <div className='onboarding-progress-bar-outer'>
          <div
            className='onboarding-progress-bar-inner'
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className='onboarding-steps'>
          <OnboardingStep
            icon={Camera}
            label='תמונת תעודת זהות'
            done={ob.id_image_uploaded}
            date={ob.id_image_uploaded_at}
            docInfo={docs.id_image}
            showImage
            accept='image/*'
            uploading={uploading.id_image}
            onUpload={(f) => handleUpload("id_image", f)}
            onDelete={() => handleDelete("id_image")}
          />
          <OnboardingStep
            icon={FileText}
            label='חוזה חתום'
            done={ob.contract_signed}
            date={ob.contract_signed_at}
            docInfo={docs.contract}
            accept='.pdf,application/pdf'
            uploading={uploading.contract}
            onUpload={(f) => handleUpload("contract", f)}
            onDelete={() => handleDelete("contract")}
          />
          <OnboardingStep
            icon={FileText}
            label='טופס 101 חתום'
            done={ob.form101_signed}
            date={ob.form101_signed_at}
            docInfo={docs.form101}
            accept='.pdf,application/pdf'
            uploading={uploading.form101}
            onUpload={(f) => handleUpload("form101", f)}
            onDelete={() => handleDelete("form101")}
          />
        </div>
      </div>

      <Toast
        msg={toast.msg}
        type={toast.type}
        onClose={() => setToast({ msg: "", type: "success" })}
      />
    </div>
  );
}
